import React, { ReactNode, useEffect, useState } from "react";
import Grid from "@mui/material/Grid2";
import { useTranslation } from "react-i18next";
import { Box, IconButton, Tooltip, styled } from "@mui/material";
import UploadDialog, { UploadType } from "./UploadDialog";
import DeleteDialog from "./DeleteDialog";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";

type LogoProps = {
  objectId?: string;
  logoClass: string;
  logo?: string;
  disabled?: boolean;
  defaultimage: string;
  isEdit: boolean;
  deleteButtonProps?: {
    label?: string;
    icon?: ReactNode;
    onClick?: () => void;
  };
  refetchAsset?: () => void;
};

const StyledImage = styled("img")(({ theme }) => ({
  aspectRatio: "1",
  width: "100%",
}));

const Logo = (props: LogoProps) => {
  const {
    objectId,
    logoClass = "",
    logo,
    defaultimage,
    disabled = false,
    isEdit = false,
    refetchAsset = () => {},
  } = props;
  const { t } = useTranslation();
  const [image, setImage] = useState<string>(defaultimage);
  const [openUploadDialog, setOpenUploadDialog] = useState<boolean>(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const canDelete = image !== defaultimage && !disabled;
  const canUpload = typeof objectId !== "undefined" && !disabled;
  useEffect(() => {
    if (logo) {
      setImage(__symphony_env__.REACT_APP_SERVER_URL + logo);
    }
  }, [logo]);

  const onCloseDeleteDialog = (action: boolean) => {
    if (action) setImage(defaultimage);
    refetchAsset();
    setOpenDeleteDialog(false);
  };
  const onCloseUploadDialog = (path: string) => {
    if (path != "") {
      setImage(
        `${__symphony_env__.REACT_APP_SERVER_URL}/symphony-api/provisioning/image/${path}`
      );
    }
    setOpenUploadDialog(false);
  };

  const replaceImage = () => {
    setImage(defaultimage);
  };
  const deleteButtonProps = {
    label: "Remove logo",
    onClick: setOpenDeleteDialog,
    icon: <HighlightOffIcon />,
    ...props.deleteButtonProps,
  };
  return (
    <>
      <Box sx={{ width: "12rem" }}>
        <Grid
          container
          sx={{ width: "100%", justifyContent: "center", position: "relative" }}
        >
          <StyledImage key={image} src={image} onError={replaceImage} />
          <Grid
            sx={(theme) => ({
              position: "absolute",
              right: theme.spacing(1),
              bottom: theme.spacing(1),
            })}
          >
            {canDelete && (
              <Tooltip title={t(deleteButtonProps.label)} arrow>
                <span>
                  <IconButton
                    onClick={() => deleteButtonProps.onClick(true)}
                    sx={(theme) => ({
                      margin: "0 5px",
                      backgroundColor: theme.palette.common.grey.g474a53,
                      color: theme.palette.common.white,
                      opacity: 0.7,
                      "&:hover": {
                        backgroundColor: theme.palette.common.grey.light,
                      },
                    })}
                    disabled={!canDelete}
                  >
                    {deleteButtonProps.icon}
                  </IconButton>
                </span>
              </Tooltip>
            )}

            {canUpload && (
              <Tooltip title={t("Upload logo")} arrow>
                <span>
                  <IconButton
                    onClick={() => setOpenUploadDialog(true)}
                    sx={(theme) => ({
                      margin: "0 5px",
                      background: theme.palette.common.white,
                      color: theme.palette.common.black,
                      "&:hover": {
                        backgroundColor: theme.palette.common.grey.light,
                      },
                    })}
                    disabled={!canUpload}
                    data-testid='upload-logo-button'
                  >
                    <AddPhotoAlternateIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Grid>
        </Grid>
      </Box>
      <UploadDialog
        open={openUploadDialog}
        logoOnClose={onCloseUploadDialog}
        objectId={objectId}
        logoClass={logoClass}
        uploadType={UploadType.Logo}
        acceptedFiles={{ "image/*": [".jpeg", ".jpg", ".png"] }}
        title={t("Upload Logo")}
        refetch={refetchAsset}
      ></UploadDialog>
      <DeleteDialog
        open={openDeleteDialog}
        onClose={onCloseDeleteDialog}
        objectId={objectId}
        isEdit={isEdit}
        logoClass={logoClass}
      ></DeleteDialog>
    </>
  );
};

export default Logo;
